<template>
  <div class="award-detail">
    <el-form size="small" :inline="true" class="query-form">
      <el-form-item label="统计月份">
        <el-date-picker
          v-model="monthRange"
          type="monthrange"
          range-separator="至"
          start-placeholder="开始月"
          end-placeholder="结束月"
          value-format="yyyy-MM"
          :clearable="false"
          style="width: 260px"
        />
      </el-form-item>
      <el-form-item label="司机编号">
        <el-input
          v-model="queryVo.driverNo"
          clearable
          placeholder="选填"
          style="width: 140px"
          @keyup.enter.native="query(1)"
        />
      </el-form-item>
      <el-form-item label="任务分类">
        <el-select v-model="queryVo.flCode" clearable placeholder="全部" style="width: 140px">
          <el-option
            v-for="item in flCodeOpts"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="query(1)">查询</el-button>
      </el-form-item>
    </el-form>

    <el-alert
      v-if="queryVo.flCode"
      title="已按任务分类筛选时，仅展示任务参与相关数据；好评/时效与实发明细请清空分类后查看。"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 10px"
    />

    <el-table :data="list" size="small" stripe border v-loading="loading">
      <el-table-column prop="month" label="月份" width="90" />
      <el-table-column prop="driverNo" label="司机编号" width="100" />
      <el-table-column prop="driverName" label="司机姓名" width="100" />
      <el-table-column prop="participateCount" label="参与任务" width="90" />
      <el-table-column prop="reachCount" label="获奖次数" width="90" />
      <el-table-column prop="exchangeCount" label="兑奖次数" width="90" />
      <el-table-column label="任务兑奖金额" min-width="110">
        <template slot-scope="{ row }"> {{ row.taskAwardAmount | moneyFormat }}元 </template>
      </el-table-column>
      <el-table-column label="好评/时效" min-width="110">
        <template slot-scope="{ row }"> {{ row.specialAwardAmount | moneyFormat }}元 </template>
      </el-table-column>
      <el-table-column label="实发奖励" min-width="110">
        <template slot-scope="{ row }"> {{ row.paidAwardAmount | moneyFormat }}元 </template>
      </el-table-column>
      <el-table-column label="合计" min-width="110">
        <template slot-scope="{ row }">
          <span class="total">{{ row.totalAwardAmount | moneyFormat }}元</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openRecords(row)">流水</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="page">
      <el-pagination
        background
        layout="total, prev, pager, next, jumper"
        :current-page="pageNo"
        :page-size="pageSize"
        :total="total"
        @current-change="query"
      />
    </div>

    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawerVisible"
      size="720px"
      direction="rtl"
      custom-class="award-record-drawer"
    >
      <div class="drawer-body" v-loading="recordLoading">
        <div class="section-title">任务参与</div>
        <el-table :data="taskRecords" size="small" stripe border max-height="260">
          <el-table-column prop="taskName" label="任务" min-width="140" show-overflow-tooltip />
          <el-table-column prop="flCode" label="分类" width="90">
            <template slot-scope="{ row }">
              {{ row.flCode | enumTransfer(flCodeOpts) }}
            </template>
          </el-table-column>
          <el-table-column label="期间" width="160">
            <template slot-scope="{ row }">
              {{ row.startDate }} ~ {{ row.endDate }}
            </template>
          </el-table-column>
          <el-table-column prop="rankNo" label="排名" width="60" />
          <el-table-column label="达标" width="70">
            <template slot-scope="{ row }">
              {{ row.reached ? "是" : "否" }}
            </template>
          </el-table-column>
          <el-table-column label="兑奖" width="70">
            <template slot-scope="{ row }">
              {{ row.excharged ? "是" : "否" }}
            </template>
          </el-table-column>
          <el-table-column label="奖金" width="90">
            <template slot-scope="{ row }">
              {{ row.award | moneyFormat }}元
            </template>
          </el-table-column>
        </el-table>

        <div class="section-title">好评 / 时效奖励</div>
        <el-table :data="specialRecords" size="small" stripe border max-height="220">
          <el-table-column label="类型" width="100">
            <template slot-scope="{ row }">
              {{ specialTypeText(row.type) }}
            </template>
          </el-table-column>
          <el-table-column label="期间" width="160">
            <template slot-scope="{ row }">
              {{ row.startDate }} ~ {{ row.endDate }}
            </template>
          </el-table-column>
          <el-table-column prop="value" label="考评值" width="80" />
          <el-table-column label="标准" width="90">
            <template slot-scope="{ row }">
              {{ row.curAwardStandard | moneyFormat }}元
            </template>
          </el-table-column>
          <el-table-column label="奖金" width="90">
            <template slot-scope="{ row }">
              {{ row.curTotalAward | moneyFormat }}元
            </template>
          </el-table-column>
          <el-table-column label="结算" width="70">
            <template slot-scope="{ row }">
              {{ row.settled ? "是" : "否" }}
            </template>
          </el-table-column>
          <el-table-column prop="invalidateReason" label="作废原因" min-width="120" show-overflow-tooltip />
        </el-table>

        <div class="section-title">结算实发</div>
        <el-table :data="paidRecords" size="small" stripe border max-height="220">
          <el-table-column prop="date" label="日期" width="110" />
          <el-table-column label="金额" width="100">
            <template slot-scope="{ row }">
              {{ row.amount | moneyFormat }}元
            </template>
          </el-table-column>
          <el-table-column prop="awardType" label="类型" width="140" />
          <el-table-column prop="remark" label="说明" min-width="160" show-overflow-tooltip />
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getAwardStatDriverPage, getAwardStatDriverRecords } from "@/api";
import { DateUtil } from "@/util";

function defaultMonthRange() {
  const end = DateUtil.format(new Date(), "yyyy-MM");
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 5);
  const start = DateUtil.format(startDate, "yyyy-MM");
  return [start, end];
}

const SPECIAL_TYPE_MAP = {
  GOOD_EVALUATION: "好评奖励",
  TIMELY_PERFORMANCE: "时效奖励",
  COMMON_TASK: "日常任务"
};

export default {
  name: "AwardDetail",
  computed: {
    ...mapState({
      flCodeOpts: (state) => state.reward.flCodeOpts
    }),
    drawerTitle() {
      const r = this.currentRow || {};
      return `${r.driverName || ""}（${r.driverNo || ""}）· ${r.month || ""} 奖励流水`;
    }
  },
  data() {
    return {
      monthRange: defaultMonthRange(),
      queryVo: {
        driverNo: "",
        flCode: ""
      },
      list: [],
      loading: false,
      pageNo: 1,
      pageSize: 20,
      total: 0,
      drawerVisible: false,
      recordLoading: false,
      currentRow: null,
      taskRecords: [],
      specialRecords: [],
      paidRecords: []
    };
  },
  mounted() {},
  methods: {
    specialTypeText(type) {
      return SPECIAL_TYPE_MAP[type] || type || "-";
    },
    query(pageNo) {
      if (pageNo) {
        this.pageNo = pageNo;
      }
      const [startMonth, endMonth] = this.monthRange || [];
      this.loading = true;
      getAwardStatDriverPage({
        startMonth,
        endMonth,
        driverNo: this.queryVo.driverNo || undefined,
        flCode: this.queryVo.flCode || undefined,
        pageNo: this.pageNo,
        pageSize: this.pageSize
      })
        .then((res) => {
          const data = res.data || {};
          this.list = data.records || [];
          this.total = data.total || 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openRecords(row) {
      this.currentRow = row;
      this.drawerVisible = true;
      this.recordLoading = true;
      this.taskRecords = [];
      this.specialRecords = [];
      this.paidRecords = [];
      getAwardStatDriverRecords({
        driverId: row.driverId,
        month: row.month,
        flCode: this.queryVo.flCode || undefined
      })
        .then((res) => {
          const data = res.data || {};
          this.taskRecords = data.taskRecords || [];
          this.specialRecords = data.specialRecords || [];
          this.paidRecords = data.paidRecords || [];
        })
        .finally(() => {
          this.recordLoading = false;
        });
    }
  }
};
</script>

<style lang="less" scoped>
.award-detail {
  .query-form {
    margin-bottom: 8px;
  }

  .total {
    color: #e6a23c;
    font-weight: 600;
  }

  .page {
    margin-top: 12px;
    text-align: right;
  }

  .drawer-body {
    padding: 0 16px 24px;

    .section-title {
      margin: 12px 0 8px;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
  }
}
</style>
